"use client";

import { CalendarDays, BookOpenCheck, Sparkles, type LucideIcon } from "lucide-react";
import Screenshot from "../Screenshot";

// Three product pillars as stacked editorial blocks: heading copy at
// reading width, then the screenshot at full section width below it.
// Round 3 replaced the old alternating 5/7 side-by-side grid because the
// dense app screens were unreadable at ~640px on laptops and worse on
// phones; full-width rendering roughly doubles the laptop size, and the
// Screenshot `expandable` overlay covers phone legibility. Section id
// "how-it-works" is the shared fragment target for both the hero's
// "See how it works" CTA and the top-nav "How it works" link (which
// resolves to "/#how-it-works" from any route).

interface Pillar {
  Icon: LucideIcon;
  eyebrow: string;
  title: string;
  body: string;
  screenshot: string;
  screenshotAlt: string;
  screenshotWidth: number;
  screenshotHeight: number;
}

const PILLARS: Pillar[] = [
  {
    Icon: CalendarDays,
    eyebrow: "Plan",
    title: "A study week that fits your life.",
    body: "Maren reads your test date, your hours, and your resources, then builds the week. When life moves, the plan moves with you.",
    screenshot: "/marketing/screen-plan.png",
    screenshotAlt: "A weekly study plan with daily tasks and recommended resources.",
    screenshotWidth: 1882,
    screenshotHeight: 852,
  },
  {
    Icon: BookOpenCheck,
    eyebrow: "Study",
    title: "Practice that targets what you keep missing.",
    body: "Log questions in seconds. Maren tags the error, tracks the topic, and generates targeted quizzes on the weak spots you keep hitting.",
    screenshot: "/marketing/screen-quiz.png",
    screenshotAlt: "A generated practice quiz on a student's weakest topic.",
    screenshotWidth: 1035,
    screenshotHeight: 909,
  },
  {
    Icon: Sparkles,
    eyebrow: "Review",
    title: "Mistakes you actually understand.",
    body: "Paste any question you missed. Maren reads your history, walks through the reasoning, and explains the concept the way you think.",
    // Round 3: show Maren actually explaining a concept in chat, not the
    // score-trend chart -- the copy is about worked reasoning.
    screenshot: "/marketing/screen-maren-chat.png",
    screenshotAlt: "Maren explaining the reasoning behind a missed question in a chat conversation.",
    screenshotWidth: 1908,
    screenshotHeight: 880,
  },
];

export default function PillarTrio() {
  return (
    <section
      id="how-it-works"
      className="py-20 sm:py-24 bg-maren-stone dark:bg-maren-night border-t border-maren-sage/30 dark:border-maren-sage/20"
    >
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center max-w-2xl mx-auto mb-16 sm:mb-20">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-maren-green dark:text-maren-gold">
            How Maren works
          </p>
          <h2 className="mt-4 font-display font-medium tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-[1.15] text-maren-charcoal dark:text-maren-stone">
            Plan, study, and review in one place.
          </h2>
          <p className="mt-5 text-base text-maren-charcoal/70 dark:text-maren-stone/75 leading-relaxed">
            Every part of MCAT prep, joined together by what you actually missed.
          </p>
        </div>

        <div className="space-y-20 sm:space-y-24">
          {PILLARS.map(
            ({ Icon, eyebrow, title, body, screenshot, screenshotAlt, screenshotWidth, screenshotHeight }) => (
              <div key={eyebrow}>
                <div className="max-w-3xl">
                  <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] text-maren-green dark:text-maren-gold">
                    <Icon className="h-4 w-4" aria-hidden />
                    {eyebrow}
                  </div>
                  <h3 className="mt-4 font-display font-medium text-2xl sm:text-3xl lg:text-4xl leading-[1.15] text-maren-charcoal dark:text-maren-stone">
                    {title}
                  </h3>
                  <p className="mt-5 text-base sm:text-lg text-maren-charcoal/75 dark:text-maren-stone/80 leading-relaxed">
                    {body}
                  </p>
                </div>
                <div className="mt-8 max-w-5xl">
                  <Screenshot
                    src={screenshot}
                    alt={screenshotAlt}
                    width={screenshotWidth}
                    height={screenshotHeight}
                    expandable
                  />
                </div>
              </div>
            ),
          )}
        </div>
      </div>
    </section>
  );
}
